<template>
  <div class="performance-wrapper">
    <div v-if="expanded" class="performance-header-expanded">
      <div v-if="form.$model.date" class="performance-title">
        <span style="font-weight: 700">{{ workflowLabel(formatDate(form.$model.date)) }}</span>
        &nbsp;{{ workflowLabel(formatTime(form.$model.time)) }}
      </div>
      <div v-else class="performance-title">
        {{ workflowLabel(numberLabel) }}
      </div>
      <v-icon color="#333333" class="chevron" @click="expanded = !expanded">expand_less</v-icon>
      <div class="scanner-code-wrapper" :style="!form.$model.eventCode && 'display: none'">
        <div class="scanner-code-label">{{ $adminT('performances.scanner', 'Scanner app code') }}</div>
        <event-code-label admin-context :code="form.$model.eventCode || ''" />
      </div>
      <div class="action-buttons">
        <v-tooltip bottom>
          <a
            slot="activator"
            class="not-selectable button-style reports-wrapper"
            target="_blank"
            :href="
              newEventModal || !soldAtLeastOnce
                ? null
                : `/theater-organizations/${organizationSlug}/event/${form.$model.id}/report`
            "
          >
            <v-icon
              :color="newEventModal || !soldAtLeastOnce ? '#979797' : '#333333'"
              :disabled="newEventModal || !soldAtLeastOnce"
            >description</v-icon>
          </a>
          {{
            !newEventModal && soldAtLeastOnce
              ? $adminT('performances.report', 'Download the performance report')
              : $adminT('performances.reportUnavailable', 'Report not available until at least one ticket is sold')
          }}
        </v-tooltip>
        <v-tooltip bottom>
          <div
            slot="activator"
            class="event-applications"
            :class="{ 'event-applications-disabled': salesChannelsDisabled }"
          >
            <img
              :alt="$adminT('performances.applications', 'applications')"
              src="/assets/applications-open.svg"
              style="height: fit-content"
            >
            <v-switch
              v-model="form.$model.applicationsOpen"
              :disabled="salesChannelsDisabled"
              hide-details
              style="margin: 0"
              inset
            />
          </div>
          {{ $adminT('performances.allChannels', 'Sell tickets through all channels') }}
        </v-tooltip>
        <v-tooltip bottom>
          <div
            slot="activator"
            class="event-applications"
            :class="{ 'event-applications-disabled': salesChannelsDisabled }"
          >
            <img :alt="$adminT('performances.applications', 'applications')" src="/assets/Webshop-online.svg" style="height: fit-content">
            <v-switch
              v-model="form.$model.metadata.theaterOnlineSales"
              :disabled="salesChannelsDisabled"
              hide-details
              style="margin: 0"
              inset
            />
          </div>
          {{ $adminT('performances.onlineChannels', 'Sell tickets online and at outlets') }}
        </v-tooltip>
        <div
          v-if="form.$model.from > new Date().toISOString() || newEventModal"
          style="display: flex; gap: 4px"
        >
          <v-tooltip bottom>
            <button
              slot="activator"
              class="not-selectable button-style duplicate-wrapper"
              style="color: #0277bd"
              @click="$emit('duplicatePerformance', form.$model)"
            >
              <img
                :alt="$adminT('performances.duplicateIcon', 'Duplicate icon')"
                src="/assets/duplicate-performance.svg"
                style="height: fit-content"
              >
            </button>
            {{ $adminT('performances.duplicate', 'Duplicate performance') }}
          </v-tooltip>
          <v-tooltip bottom>
            <button
              slot="activator"
              class="not-selectable button-style delete-wrapper"
              :disabled="soldAtLeastOnce"
              @click="$emit('removePerformance', form.$model.id)"
            >
              <v-icon :color="soldAtLeastOnce ? '#979797' : '#DB384C'">delete_outline</v-icon>
            </button>
            {{ !soldAtLeastOnce ? $adminT('performances.delete', 'Delete this performance') : $adminT('performances.deleteUnavailable', 'Delete unavailable') }}
          </v-tooltip>
        </div>
        <v-menu
          v-if="showStatusMenu"
          v-model="statusMenuOpen"
          offset-y
          left
          nudge-bottom="8"
          content-class="performance-actions-menu"
        >
          <button slot="activator" type="button" class="not-selectable button-style menu-trigger">
            <v-icon color="#333333">more_vert</v-icon>
          </button>
          <div class="performance-menu-list">
            <button
              v-if="!soldOut && !isCanceled"
              type="button"
              class="performance-menu-item"
              @click="handleMarkAsSoldOut"
            >
              <img
                :alt="$adminT('performances.soldAlt', 'Mark as sold out')"
                class="performance-menu-icon"
                src="/assets/sidebar-icons/tickets-sold-out.svg"
              >
              <span class="performance-menu-label">{{ $adminT('performances.sold', 'Mark as "Sold out"') }}</span>
            </button>
            <button
              v-if="soldOut && !isCanceled"
              type="button"
              class="performance-menu-item"
              @click="handleResumeSales"
            >
              <img
                src="/assets/applications-open.svg"
                :alt="$adminT('performances.resume', 'Resume sales')"
                class="performance-menu-icon"
              >
              <span class="performance-menu-label">{{ $adminT('performances.resume', 'Resume sales') }}</span>
            </button>
            <button
              v-if="!isCanceled"
              type="button"
              class="performance-menu-item"
              @click="handleCancelPerformance"
            >
              <img
                :alt="$adminT('performances.cancelPerformance', 'Cancel performance')"
                class="performance-menu-icon"
                src="/assets/sidebar-icons/cancel-event.svg"
              >
              <span class="performance-menu-label">{{ $adminT('performances.cancelPerformance', 'Cancel performance') }}</span>
            </button>
          </div>
        </v-menu>
      </div>
    </div>
    <div v-else class="performance-header-collapsed">
      <div v-if="form.$model.date" class="performance-title">
        <span style="font-weight: 700">{{ workflowLabel(formatDate(form.$model.date)) }}</span>
        {{ workflowLabel(formatTime(form.$model.time)) }}
      </div>
      <div v-else class="performance-title">
        {{ workflowLabel(numberLabel) }}
      </div>
      <v-icon color="#333333" @click="expanded = !expanded">expand_more</v-icon>
    </div>
    <div v-if="statusBanner" class="performance-status-banner">
      <v-icon size="20" color="#BD2A3C">info</v-icon>
      <span class="performance-status-text">{{ workflowLabel(statusBanner) }}</span>
    </div>
    <div class="performance-details" :style="!expanded && 'display: none'">
      <div class="performance-date">
        <date-field
          v-model="form.$model.date"
          admin-context
          :min="new Date().toISOString()"
          style="width: 100%"
          :label="$adminT('performances.date', 'Date*')"
          :validator="vs.date"
        />
        <time-field
          v-model="form.$model.time"
          admin-context
          style="width: 100%"
          :min="getMinTime"
          :label="$adminT('performances.time', 'Time*')"
          :validator="vs.time"
        />
      </div>
      <select-field
        v-model="form.$model.performanceType"
        admin-context
        :disabled="soldAtLeastOnce"
        :label="$adminT('performances.type', 'Type of performance*')"
        :validator="vs.performanceType"
        :items="workflowUi(performanceTypeItems)"
        item-render="label"
        item-select="value"
      />
      <select-field
        v-model="form.$model.venueId"
        admin-context
        :disabled="soldAtLeastOnce"
        :items="workflowUi(venues)"
        item-render="name"
        item-select="id"
        :label="$adminT('performances.odeum', 'Odeum*')"
        :validator="vs.venueId"
      />
      <text-field
        v-if="hasVenueSelected && isVenueWithoutSeating"
        v-model.number="form.$model.audienceCapacity"
        admin-context
        type="number"
        step="1"
        :label="$adminT('performances.available', 'Tickets available*')"
        :validator="vs.audienceCapacity"
      />
      <div
        v-if="hasVenueSelected && !isVenueWithoutSeating"
        class="additional-capacity-wrapper"
        :style="isMobile && 'flex-direction: column'"
      >
        <div class="switch-wrapper" style="padding-bottom: 14px">
          <p class="additional-capacity-switch-label">{{ $adminT('performances.extraSeats', 'Add extra seats without numeration') }}</p>
          <v-switch
            id="additional-capacity-switch"
            v-model="form.$model.addExtraSeats"
            hide-details
            style="margin: 0"
            inset
          />
        </div>
        <text-field
          v-model.number="form.$model.additionalCapacity"
          admin-context
          type="number"
          step="1"
          :min="form.$model.totalAdditionalCapacityTicketsSold"
          :label="$adminT('performances.amount', 'Amount')"
          :validator="vs.additionalCapacity"
          :disabled="!form.$model.addExtraSeats"
        />
      </div>
      <div class="ticket-type-wrapper" :style="isMobile && 'flex-direction: column'">
        <select-field-multiple
          v-model="form.$model.orderItemTemplateIds"
          admin-context
          :validator="hasVenueSelected ? vs.orderItemTemplateIds : null"
          :disabled="soldOut || !hasVenueSelected"
          :hint="workflowLabel(ticketTypesHint)"
          :no-data-text="$adminT('performances.noTickets', 'No ticket types for this odeum yet – use Create ticket to add one.')"
          style="width: 100%"
          :label="$adminT('performances.ticketTypes', 'Type of Tickets*')"
          :items="
            workflowUi(ticketTypeItems.map(item => {
              return {
                ...item,
                displayName: `${item.name}${item.archivedAt ? $adminT('performances.archived', ' (archived)') : ''} - ${
                  item.price
                } EUR`,
                disabled: orderItemTemplateIdsSoldAtLeastOnce.includes(item.id),
              }
            }))
          "
          item-render="displayName"
          item-select="id"
        />
        <v-tooltip bottom :disabled="hasVenueSelected">
          <div slot="activator">
            <main-button
              flat
              style="height: fit-content"
              :disabled="soldOut || !hasVenueSelected"
              @click="handleOpenCreateTicketModal"
            >
              <span class="add-symbol">+</span>{{ $adminT('performances.createTicket', 'Create ticket') }}
            </main-button>
          </div>
          {{ $adminT('performances.ticketOdeumHint', 'Select an odeum first – a new ticket type is created for the selected odeum.') }}
        </v-tooltip>
      </div>
      <select-field-multiple
        v-if="displayPromoCodes()"
        key="name"
        v-model="promoCodeNames"
        admin-context
        :disabled="soldAtLeastOnce || promoCodesAreSelectable"
        style="width: 100%"
        :label="$adminT('performances.promoCodes', 'Promo code(s) – Applicable only when a regular ticket is selected')"
        :items="workflowUi(categorizePromoCodes)"
        @change="item => handlePromoCodeClick(item)"
      />
      <select-field-multiple
        v-if="hasSomeTicketSalesChannelsAvailable"
        v-model="selectedTicketSellers"
        admin-context
        item-select="id"
        item-render="name"
        :disabled="soldAtLeastOnce"
        style="width: 100%"
        :label="$adminT('performances.sellers', 'Ticket sellers')"
        :items="workflowUi(ticketSellers)"
        @change="item => handleTicketSellerClick(item)"
      />
      <div class="subscription-info-wrapper" :class="{ 'warning-hint': showNoSubscriptionsInfo }">
        <checkbox
          v-model="form.$model.isIncludedInSubscription"
          color="#0277BD"
          style="margin-bottom: 1rem"
          :hint="workflowLabel(subscriptionWarningMessage)"
        >
          <div>{{ $adminT('performances.subscription', 'Included in subscription') }}</div>
        </checkbox>
      </div>
      <p v-if="hasSomeTicketSalesChannelsAvailable && isTisakInTicketSellers" class="tisak-term">
        {{ $adminT('performances.tisakFee', 'TISAK outlets will charge ticket buyers an additional ticket fee') }}
      </p>
      <ticket-type-create-modal
        v-if="openCreateTicketModal"
        :organization-venues="venues"
        :preselected-venue-id="form.$model.venueId"
        @close="handleOpenCreateTicketModal"
        @closeModal="handleOpenCreateTicketModal"
        @createTicket="handleCreateTicketType"
      />
    </div>
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="workflowLabel(localizedSnackbarMessage)"
      :color="snackbar.color"
    />
    <v-dialog v-model="cancelConfirmationOpen" persistent max-width="512">
      <div class="cancel-performance-dialog">
        <div class="cancel-performance-dialog-header">
          <div class="cancel-performance-dialog-copy">
            <p class="cancel-performance-dialog-title">
              {{ workflowLabel(cancelQuestion) }}
            </p>
            <p class="cancel-performance-dialog-description">{{ $adminT('performances.irreversible', 'This action cannot be undone.') }}</p>
          </div>
          <button
            type="button"
            class="cancel-performance-dialog-close"
            @click="cancelConfirmationOpen = false"
          >
            <v-icon color="#979797" size="24">close</v-icon>
          </button>
        </div>
        <div class="cancel-performance-dialog-actions">
          <button type="button" class="cancel-performance-dialog-button" @click="confirmCancelPerformance">
            {{ $adminT('performances.confirmCancel', 'CANCEL PERFORMANCE') }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import TicketTypeCreateModal from '@/components/organizations/ticket-types/TicketTypeCreateModal.vue'
import { CREATE_ORGANIZATION_ORDER_ITEM_TEMPLATES } from '@/graphql/orderItemTemplate.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { EventPerformanceType } from '@/types'
import moment from 'moment'
import { tisakTicketSeller } from '@/utils'
import EventCodeLabel from '@/components/shared/event-code-label.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'performance',
  components: {
    TicketTypeCreateModal,
    SimpleSnackbar,
    EventCodeLabel,
  },
  props: {
    initialValues: {
      type: Object,
      default: () => {},
    },
    index: {
      type: Number,
      default: 0,
    },
    vs: {
      type: Object,
      default: () => {},
    },
    venues: {
      type: Array,
      default: () => [],
    },
    promoCodes: {
      type: Array,
      default: () => [],
    },
    ticketTypes: {
      type: Array,
      default: () => [],
    },
    soldOut: {
      type: Boolean,
      default: false,
    },
    soldAtLeastOnce: {
      type: Boolean,
      default: false,
    },
    orderItemTemplateIdsSoldAtLeastOnce: {
      type: Array,
      default: () => [],
    },
    newEventModal: {
      type: Boolean,
      default: true,
    },
    isCanceled: {
      type: Boolean,
      default: false,
    },
    organizationSlug: String,
    ticketSellers: {
      type: Array,
      default: () => [],
    },
    activeSubscriptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const isArtKinoOrganization = this.organizationSlug === 'art-kino-2599'
    const artKinoPerformanceTypes = ['REGULAR_SCREENING', 'MATINEE', 'TALK']
    const performanceTypes = isArtKinoOrganization
      ? Object.keys(EventPerformanceType)
      : Object.keys(EventPerformanceType).filter(key => !artKinoPerformanceTypes.includes(key))

    return {
      form: this.initialValues,
      openCreateTicketModal: false,
      expanded: !this.soldOut,
      promoCodeNames: [],
      snackbar: {
        open: false,
        message: '',
        color: 'primary',
      },
      performanceTypes,
      selectedTicketSellers: this.initialValues ? this.initialValues.$model.ticketSellerIds : [],
      isTisakInTicketSellers: !!this.ticketSellers.find(
        item => item.name.toLowerCase() === tisakTicketSeller,
      ),
      statusMenuOpen: false,
      cancelConfirmationOpen: false,
    }
  },
  computed: {
    localizedSnackbarMessage() {
      switch (this.snackbar.message) {
      case 'performances.ticketSuccess':
        return this.$adminT('performances.ticketSuccess', 'Successfully created ticket type!')
      case 'performances.ticketError':
        return this.$adminT('performances.ticketError', 'Oops, something went wrong!')
      default:
        return ''
      }
    },

    numberLabel() {
      return this.$adminT('performances.number', 'performance {{number}}', { number: this.index + 1 })
    },
    cancelQuestion() {
      return this.$adminT('performances.cancelQuestion', 'Are you sure you want to cancel performance {{performanceLabel}}?', { performanceLabel: this.performanceLabel })
    },
    performanceTypeItems() {
      const labels = {
        PREMIERE: this.$adminT('performances.typePremiere', 'PREMIERE'),
        REPRISE: this.$adminT('performances.typeReprise', 'REPRISE'),
        GUEST: this.$adminT('performances.typeGuest', 'GUEST'),
        REGULAR_SCREENING: this.$adminT('performances.typeRegularScreening', 'REGULAR_SCREENING'),
        MATINEE: this.$adminT('performances.typeMatinee', 'MATINEE'),
        TALK: this.$adminT('performances.typeTalk', 'TALK'),
      }

      return this.performanceTypes.map(value => ({ value, label: labels[value] }))
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },
    getMinTime() {
      if (moment(this.form.$model.date).isSame(moment(), 'day')) {
        const now = new Date()

        return `${now.getHours()}:${now.getMinutes()}`
      }

      return undefined
    },
    hasVenueSelected() {
      return !!this.form.$model.venueId
    },
    // Ticket types always belong to an odeum, so the list can only ever be empty before one is
    // picked. Say so instead of showing an empty select.
    ticketTypesHint() {
      return this.hasVenueSelected
        ? undefined
        : this.$adminT('performances.odeumHint', 'Select an odeum first – ticket types belong to a specific odeum.')
    },
    isVenueWithoutSeating() {
      if (!this.form.$model.venueId) {
        return true
      }

      return this.venues
        .filter(v => !v.seats || !v.predefinedSections)
        .map(v => v.id)
        .includes(this.form.$model.venueId)
    },
    ticketTypeItems: function () {
      // Archived ticket types stay listed while this performance still uses them, so they can be
      // reviewed or removed, but they are never offered for a performance that does not have them.
      const selectedOrderItemTemplateIds = this.form.$model.orderItemTemplateIds || []

      return this.ticketTypes.filter(
        ticket =>
          ticket.venueId === (parseInt(this.form.$model.venueId) || null) &&
          (!ticket.archivedAt || selectedOrderItemTemplateIds.includes(ticket.id)),
      )
    },
    categorizePromoCodes: function () {
      const categorizedPromoCodes = []
      this.promoCodes.map(pc => {
        const existingCategory = categorizedPromoCodes.find(cpc => cpc.name === pc.name)
        if (!existingCategory) {
          categorizedPromoCodes.push(pc.name)
        }
      })

      return categorizedPromoCodes
    },
    promoCodesAreSelectable: function () {
      const venueRegularTicket = this.ticketTypeItems.find(
        ticket => !ticket.membership && !ticket.ticketSeller && ticket.prepaidCoupon,
      )

      return (
        !this.form.$model.orderItemTemplateIds ||
        (!!this.form.$model.orderItemTemplateIds.length &&
          !!venueRegularTicket &&
          !this.form.$model.orderItemTemplateIds.includes(venueRegularTicket.id))
      )
    },
    hasSomeTicketSalesChannelsAvailable: function () {
      return (this.form.$model.orderItemTemplateIds || []).some(id => {
        const ticket = this.ticketTypeItems.find(item => item.id === id)

        return ticket && ticket.salesChannel
      })
    },
    showNoSubscriptionsInfo() {
      const included = !!this.form.$model.isIncludedInSubscription
      const venueId = Number(this.form.$model.venueId)
      const performanceDate = this.form.$model.date ? moment(this.form.$model.date).startOf('day') : null
      const hasActive = this.activeSubscriptions.some(subscription => {
        if (Number(subscription.venueId) !== venueId) {
          return false
        }

        // A new performance may not have a date yet, so avoid a misleading
        // warning while the organizer is still filling out the form.
        if (!performanceDate || !performanceDate.isValid()) {
          return true
        }

        const validFrom = subscription.valid_from
          ? moment(subscription.valid_from).startOf('day')
          : null
        const validTo = subscription.valid_to ? moment(subscription.valid_to).endOf('day') : null

        return (
          (!validFrom || performanceDate.valueOf() >= validFrom.valueOf()) &&
          (!validTo || performanceDate.valueOf() <= validTo.valueOf())
        )
      })

      return included && venueId && !hasActive
    },
    subscriptionWarningMessage() {
      return this.showNoSubscriptionsInfo
        ? this.$adminT('performances.noSubscriptions', 'There are currently no active subscriptions linked to the selected venue.')
        : ''
    },
    salesChannelsDisabled() {
      return this.soldOut || this.isCanceled
    },
    performanceLabel() {
      if (this.form.$model.date) {
        return `${this.formatDate(this.form.$model.date)} ${this.formatTime(this.form.$model.time)}`
      }

      return `${this.index + 1}`
    },
    statusBanner() {
      if (this.isCanceled) {
        return this.$adminT('performances.canceled', 'Performance canceled')
      }

      if (this.soldOut) {
        return this.$adminT('performances.soldBanner', 'Marked as sold out')
      }

      return ''
    },
    showStatusMenu() {
      return (
        !this.newEventModal && !this.isCanceled && Number.isInteger(Number(this.form.$model.id))
      )
    },
  },
  watch: {
    'form.$model.venueId'(newVal, oldVal) {
      if (newVal !== oldVal && this.form.$model.orderItemTemplateIds) {
        this.form.$model.orderItemTemplateIds = []
      }
    },
    'form.$model.addExtraSeats'(isToggledOn) {
      if (!isToggledOn) {
        this.form.$model.additionalCapacity = null
      }
    },
    'form.$model.applicationsOpen'(toggleState) {
      this.form.$model.metadata = { theaterOnlineSales: toggleState }
    },
    'form.$model.metadata.theaterOnlineSales'(toggleState) {
      if (toggleState) {
        this.form.$model.applicationsOpen = toggleState
      }
    },
    hasSomeTicketSalesChannelsAvailable: {
      handler(val) {
        if (!val && this.form.$model.ticketSellerIds) {
          this.$emit('removeSelectedTicketSellers', this.form.$model.id)
          this.selectedTicketSellers = []
        }
      },
    },
  },
  methods: {
    handlePromoCodeClick() {
      return (this.form.$model.promoCodeIds = this.promoCodes
        .map(pc => {
          if (this.promoCodeNames.includes(pc.name)) {
            return pc.id
          }
        })
        .filter(Boolean))
    },
    handleTicketSellerClick() {
      this.form.$model.ticketSellerIds = this.ticketSellers
        .filter(item => this.selectedTicketSellers.includes(item.id))
        .map(item => Number(item.id))
    },
    displayPromoCodes() {
      return !Number.isInteger(Number(this.form.$model.id)) && !!this.promoCodes.length
    },
    prettifyDate(date, time) {
      if (!date) {
        return false
      }
      const onlyDate = moment(date)
      const timeFormat = moment(time, 'HH:mm')

      return onlyDate
        .set({ hour: timeFormat.get('hour'), minute: timeFormat.get('minute') })
        .format('DD.MM.YYYY - HH:mm')
    },
    formatDate(date) {
      if (!date) {
        return ''
      }

      return moment(date).format('DD.MM.YYYY.')
    },
    formatTime(time) {
      if (!time) {
        return ''
      }

      return moment(time, 'HH:mm').format('HH:mm')
    },
    handleOpenCreateTicketModal() {
      this.openCreateTicketModal = !this.openCreateTicketModal
    },
    handleMarkAsSoldOut() {
      this.statusMenuOpen = false
      this.$emit('markPerformanceSoldOut', this.form.$model.id)
    },
    handleResumeSales() {
      this.statusMenuOpen = false
      this.$emit('resumePerformanceSales', this.form.$model.id)
    },
    handleCancelPerformance() {
      this.statusMenuOpen = false
      this.cancelConfirmationOpen = true
    },
    confirmCancelPerformance() {
      this.cancelConfirmationOpen = false
      this.$emit('cancelPerformance', this.form.$model.id)
    },
    setTicketVenueSections(ticketInfo) {
      const selectedVenue = this.venues.find(venue => venue.id == ticketInfo.venueId)

      if (
        selectedVenue.seats &&
        selectedVenue.predefinedSections &&
        (!ticketInfo.venueSection.length || ticketInfo.venueSection.includes('All'))
      ) {
        ticketInfo.venueSection = Object.keys({ ...selectedVenue.predefinedSections })
      }

      return ticketInfo
    },
    async handleCreateTicketType(ticketInfo) {
      this.openCreateTicketModal = !this.openCreateTicketModal
      const orderItemTemplateInput = this.setTicketVenueSections(ticketInfo)
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_ORDER_ITEM_TEMPLATES,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplateInput,
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          message: 'performances.ticketError',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          message: 'performances.ticketSuccess',
        }
        this.selected = []
        this.$emit('refetchOrderItemAndVenue')
      }
    },
  },
}
</script>

<style scoped>
.ticket-type-wrapper {
  display: flex;
  justify-content: space-between;
  column-gap: 1.5rem;
}
.performance-wrapper {
  margin-bottom: 32px;
  border: solid 2px #d9d9d9;
  border-radius: 8px;
  max-width: 684px;
}
.title-n-buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f5f5f5;
  padding: 1.5rem;
  border-radius: 4px;
}

.performance-title {
  font-family: 'Raleway';
  font-style: normal;
  font-feature-settings: 'pnum' on, 'lnum' on;
  font-weight: 600;
  font-size: 20px;
  color: #333333;
}
.performance-buttons {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
}
.performance-buttons section {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.performance-date {
  width: 100%;
  display: flex;
  justify-content: space-between;
  column-gap: 1.5rem;
}
.performance-details {
  margin-top: 42px;
  display: flex;
  flex-direction: column;
  row-gap: 2rem;
  padding: 0 1.5rem 1.5rem;
}
.not-selectable {
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
}
.additional-capacity-switch-label {
  color: #333;
  margin: 0;
  font-family: 'Source Sans Pro';
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
}
.additional-capacity-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
}
.switch-wrapper {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}
.material-icons {
  cursor: pointer;
}
.button-style {
  font-weight: 700;
  font-size: 18px;
  font-family: Raleway;
}
.scanner-id {
  color: #666;
  font-family: Source Sans Pro;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
}
.event-applications {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  background: white;
  padding: 10px 4px 12px 16px;
  border-radius: 30px;
}
.event-applications-disabled {
  opacity: 0.6;
}
.reports-wrapper {
  background: white;
  padding: 13px 13px;
  border-radius: 100000px;
}
.delete-wrapper {
  background: white;
  border-radius: 4px 25px 25px 4px;
  padding: 13px 14px 13px 10px;
}
.duplicate-wrapper {
  background: white;
  border-radius: 25px 4px 4px 25px;
  padding: 15.5px 10px 15.5px 16px;
}
.menu-trigger {
  background: white;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  padding: 13px;
}
.menu-trigger:focus {
  outline: none;
}
.performance-menu-list {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}
.performance-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  padding: 12px;
  border: 0;
  cursor: pointer;
  text-align: left;
}
.performance-menu-item:hover {
  background: #f5fbff;
}
.performance-menu-icon {
  width: 24px;
  height: 24px;
}
.performance-menu-label {
  flex: 1 0 0;
  color: #0277bd;
  font-family: 'Raleway';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.24px;
}
.tisak-term {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #666666;
  text-indent: 1rem;
  margin-top: -0.75rem;
}

/* Make the checkbox hint yellow when showing a warning */
.warning-hint >>> .v-messages__message {
  color: #ffa600; /* amber darken-2 */
  font-size: 0.8rem;
}

>>> .performance-actions-menu {
  border-radius: 8px;
  box-shadow: 0 4px 16px 0 rgba(51, 51, 51, 0.16);
  overflow: hidden;
}
.performance-status-banner {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 16px;
  background: #fbebed;
}
.performance-status-text {
  color: #bd2a3c;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}
.cancel-performance-dialog {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 0 24px 0 rgba(51, 51, 51, 0.2);
}
.cancel-performance-dialog-header {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}
.cancel-performance-dialog-copy {
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.cancel-performance-dialog-title {
  margin: 0;
  color: #0277bd;
  font-family: 'Raleway';
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.6px;
}
.cancel-performance-dialog-description {
  margin: 0;
  color: #666;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}
.cancel-performance-dialog-close {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
.cancel-performance-dialog-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
}
.cancel-performance-dialog-button {
  display: flex;
  height: 48px;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border: 0;
  border-radius: 24px;
  background: #db384c;
  color: #fff;
  font-family: 'Raleway';
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.27px;
  text-transform: uppercase;
  cursor: pointer;
}

.performance-header-collapsed {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f5f5f5;
  padding: 1.25rem;
}

.performance-title {
  color: var(--Grey-10, #666);
  font-variant-numeric: lining-nums proportional-nums;
  font-family: 'Source Sans Pro';
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.045rem;
}

.performance-header-expanded {
  padding: 0.75rem 0;
  row-gap: 0.75rem;
  background-color: #f5f5f5;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'event-date chevron'
    'scanner-code scanner-code'
    'action-buttons action-buttons';

  .performance-title {
    grid-area: event-date;
    margin-left: 1rem;
    display: flex;
    align-items: center;
  }

  .scanner-code-wrapper {
    grid-area: scanner-code;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0 1rem;
  }

  .scanner-code-label {
    overflow: hidden;
    color: var(--Grey-10, #666);
    text-overflow: ellipsis;

    /* Body/Small */
    font-family: 'Source Sans Pro';
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 1.5rem */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .chevron {
    grid-area: chevron;
    margin-right: 1rem;
  }

  .action-buttons {
    grid-area: action-buttons;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-left: 0.5rem;
    overflow-x: auto;
  }
}

@media screen and (min-width: 950px) {
  .performance-header-expanded {
    grid-template-columns: 1fr auto auto;
    grid-template-areas:
      'event-date  scanner-code scanner-code'
      'action-buttons action-buttons chevron';

    .scanner-code-wrapper {
      padding: 0;
      margin-right: 1.5rem;
    }

    .performance-title {
      margin-left: 1.5rem;
    }

    .chevron {
      margin-right: 1.5rem;
    }
  }
}
</style>
